import {
  TranscribeMediaDocument,
  type TranscribeMediaMutation,
  type TranscribeMediaMutationVariables,
  UpdateMediaMetadataDocument,
  type UpdateMediaMetadataMutation,
  type UpdateMediaMetadataMutationVariables,
} from "@/generated/graphql";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { FiLoader, FiZap } from "react-icons/fi";
import { type TableColumn, getMediaConfig, hasTranscription } from "../config";
import { MediaPreview } from "../previews";
import type { MediaItem } from "../types";
import { AccessLevelToggle } from "./AccessLevelToggle";
import { CopyLinkButton } from "./CopyLinkButton";
import { InlineTagEditor } from "./InlineTagEditor";
import { InlineTextEditor } from "./InlineTextEditor";
import { ThreeDotMenu } from "./ThreeDotMenu";

interface MediaTableRowProps {
  /** Media item to display */
  media: MediaItem;
  /** Whether the row is selected */
  isSelected: boolean;
  /** Columns to render (from config) */
  columns: TableColumn[];
  /** Callback when selection is toggled */
  onToggleSelect: () => void;
  /** Callback to open edit modal */
  onEdit: () => void;
  /** Callback to open move modal */
  onMove: () => void;
  /** Callback to delete item */
  onDelete: () => void;
  /** Optional style for virtualization */
  style?: React.CSSProperties;
}

/**
 * MediaTableRow - Generic table row for all media types
 *
 * Renders columns dynamically based on configuration.
 * Handles inline editing for audioText and tags.
 */
export function MediaTableRow({
  media,
  isSelected,
  columns,
  onToggleSelect,
  onEdit,
  onMove,
  onDelete,
  style,
}: MediaTableRowProps) {
  const config = getMediaConfig(media.type);
  const showTranscription = hasTranscription(media.type);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const [updateMetadata] = useMutation<
    UpdateMediaMetadataMutation,
    UpdateMediaMetadataMutationVariables
  >(UpdateMediaMetadataDocument);

  const [transcribeMedia] = useMutation<TranscribeMediaMutation, TranscribeMediaMutationVariables>(
    TranscribeMediaDocument
  );

  const handleSaveAudioText = (value: string) => {
    updateMetadata({
      variables: {
        input: {
          id: media.id,
          audioText: value, // Allow empty strings to clear the field
        },
      },
    });
  };

  const handleSaveTags = (tags: string[]) => {
    updateMetadata({
      variables: {
        input: {
          id: media.id,
          tags,
        },
      },
    });
  };

  const handleTranscribe = async () => {
    setIsTranscribing(true);

    try {
      const result = await transcribeMedia({
        variables: {
          mediaId: media.id,
        },
      });

      const transcriptionResult = result.data?.transcribeMedia;

      if (transcriptionResult?.success && transcriptionResult.text) {
        // Save transcription immediately via updateMetadata
        await updateMetadata({
          variables: {
            input: {
              id: media.id,
              audioText: transcriptionResult.text,
            },
          },
        });
        toast.success(`Transcription complete! (${transcriptionResult.duration?.toFixed(1)}s)`);
      } else {
        const errorMsg = transcriptionResult?.error || "Transcription failed";
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Transcription failed";
      toast.error(errorMsg);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Get audioText if available
  const audioText = ("audioText" in media ? media.audioText : null) || "";

  // Get duration if available
  const duration = "duration" in media ? media.duration : undefined;

  // Render a specific column
  const renderColumn = (column: TableColumn) => {
    switch (column) {
      case "checkbox":
        return (
          <td key="checkbox" className="w-[1%] w-12 px-4 py-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label={`Select ${media.filename}`}
            />
          </td>
        );

      case "preview":
      case "filename":
        // Combine preview and filename into one cell
        if (column === "preview") {
          return (
            <td key="preview-filename" className="w-[30%] max-w-0 px-4 py-3">
              <MediaPreview
                url={media.url}
                filename={media.filename}
                type={media.type}
                duration={duration}
                variant="inline"
              />
            </td>
          );
        }
        // Skip filename if preview is also in columns (they're combined)
        if (columns.includes("preview")) {
          return null;
        }
        return (
          <td key="filename" className="w-[35%] px-4 py-3">
            <p className="font-medium text-gray-900 truncate">{media.filename}</p>
          </td>
        );

      case "audioText":
        return (
          <td key="audioText" className="w-[20%] min-w-32 px-4 py-3">
            {config.hasAudioText ? (
              <div className="flex items-center gap-2">
                {showTranscription && (
                  <button
                    type="button"
                    onClick={handleTranscribe}
                    disabled={isTranscribing}
                    className="group inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 px-2 py-1 text-[10px] font-medium text-blue-700 transition-all duration-200 hover:from-blue-100 hover:to-indigo-100 hover:shadow-sm disabled:from-gray-50 disabled:to-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed flex-shrink-0"
                    title={isTranscribing ? "Transcribing audio..." : "Generate AI transcription"}
                  >
                    {isTranscribing ? (
                      <>
                        <FiLoader className="h-3 w-3 animate-spin" />
                        {/* <span>Transcribing...</span> */}
                      </>
                    ) : (
                      <>
                        <FiZap className="h-3 w-3 transition-transform group-hover:scale-110" />
                        {/* <span>AI Transcribe</span> */}
                      </>
                    )}
                  </button>
                )}
                <div className="flex-1 min-w-0">
                  <InlineTextEditor
                    value={audioText}
                    placeholder="Click to add"
                    onSave={handleSaveAudioText}
                  />
                </div>
              </div>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
          </td>
        );

      case "tags":
        return (
          <td key="tags" className="w-[18%] min-w-40 px-4 py-3">
            <InlineTagEditor tags={media.tags || []} onSave={handleSaveTags} />
          </td>
        );

      case "url":
        return (
          <td key="url" className="w-[5%] px-4 py-3">
            <div className="flex items-center">
              <AccessLevelToggle media={media} />
              <CopyLinkButton url={media.url} />
            </div>
          </td>
        );

      case "actions":
        return (
          <td key="actions" className="w-[10%] px-4 py-3 text-right">
            <ThreeDotMenu media={media} onEdit={onEdit} onMove={onMove} onDelete={onDelete} />
          </td>
        );

      case "duration":
        // Duration is usually shown with filename/preview
        return null;

      default:
        return null;
    }
  };

  return (
    <tr className={`${isSelected ? "bg-blue-50" : "hover:bg-gray-50"} border-b`} style={style}>
      {columns.map(renderColumn)}
    </tr>
  );
}

export default MediaTableRow;
